import { useMemo } from "react";
import { ConfigProvider, DatePicker } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useViewport } from "../hooks/useViewport";
import { dayBounds, type DayMode } from "../lib/businessDay";
import { useIsDarkTheme } from "../store/hooks";

const { RangePicker } = DatePicker;

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  borderMed: "var(--color-shade-3)",
  ink: "var(--color-ink)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  brand: "#D97706",
  brandDeep: "var(--color-brand-deep-ink)",
  brandTint: "var(--color-brand-tint)",
  brandTint2: "var(--color-brand-tint2)",
};

// Kept for backward compatibility with callers that pass `defaultMode`
type DateMode = "day" | "month";

type Preset = "yesterday" | "today" | "week" | "thisMonth";

interface ReportDatePickerProps {
  onChange: (dates: [Dayjs, Dayjs] | null) => void;
  value?: [Dayjs | null, Dayjs | null] | null;
  defaultMode?: DateMode;
  withTime?: boolean;
  /**
   * Календарен ден (по подразбиране) или работен — денят на заведението по
   * работното време (`lib/businessDay.ts`; ненастроено → 06:00 → 05:59).
   *
   * ИЗРИЧЕН е нарочно. Този пикер го ползват и Складовата база, „Одит на
   * действията" и ФМ отчетът в Маркирането; те нормализират периода със
   * `startOf("day")`/`endOf("day")` и работен прозорец през полунощ би ги
   * накарал да питат за ДВА календарни дни.
   */
  dayMode?: DayMode;
}

// В режим „работен ден" пресетите работят с РАБОТНИ дати: в 00:23 „днес" е
// вчерашната смяна, а не новият календарен ден. В календарен режим всичко е
// както преди — `dayBounds` връща `startOf("day")` / `endOf("day")`.
function getPresetRange(p: Preset, mode: DayMode): [Dayjs, Dayjs] {
  const { dateOf, startOf, endOf } = dayBounds(mode);
  const today = dateOf(dayjs());
  if (p === "yesterday") {
    const y = today.subtract(1, "day");
    return [startOf(y), endOf(y)];
  }
  if (p === "today") return [startOf(today), endOf(today)];
  if (p === "week") return [startOf(today.subtract(6, "day")), endOf(today)];
  return [startOf(today.startOf("month")), endOf(today.endOf("month"))];
}

// Сравненията са по ДАТАТА на краищата според режима: в работен режим краят е
// 05:59 на следващата календарна дата и суровият момент би сочил чужд ден, чужд
// месец, а в последния ден на месеца — и чужда година.
function isFullMonth(range: [Dayjs, Dayjs], mode: DayMode): boolean {
  const { dateOf } = dayBounds(mode);
  const start = dateOf(range[0]);
  const end = dateOf(range[1]);
  return (
    start.isSame(start.startOf("month"), "day") &&
    end.isSame(start.endOf("month"), "day")
  );
}

function matchesPreset(range: [Dayjs, Dayjs], mode: DayMode): Preset | null {
  const { dateOf } = dayBounds(mode);
  const start = dateOf(range[0]);
  const end = dateOf(range[1]);
  const today = dateOf(dayjs());
  const yesterday = today.subtract(1, "day");
  if (start.isSame(yesterday, "day") && end.isSame(yesterday, "day"))
    return "yesterday";
  if (isFullMonth(range, mode) && start.isSame(today.startOf("month"), "day"))
    return "thisMonth";
  if (!end.isSame(today, "day")) return null;
  if (start.isSame(today, "day")) return "today";
  const spanDays = today.diff(start, "day") + 1;
  if (spanDays === 7) return "week";
  return null;
}

export const ReportDatePicker = ({
  onChange,
  value,
  withTime = false,
  dayMode = "calendar",
}: ReportDatePickerProps) => {
  const { dateOf, startOf: startOfDay, endOf: endOfDay } = dayBounds(dayMode);
  const { t } = useTranslation();
  const { isNarrow } = useViewport();
  const isDark = useIsDarkTheme();

  const currentValue: [Dayjs, Dayjs] =
    value && value[0] && value[1]
      ? [value[0], value[1]]
      : [startOfDay(dateOf(dayjs())), endOfDay(dateOf(dayjs()))];

  const activePreset = useMemo(
    () => matchesPreset(currentValue, dayMode),
    [currentValue, dayMode],
  );

  const rangeIsFullMonth = useMemo(
    () => isFullMonth(currentValue, dayMode),
    [currentValue, dayMode],
  );

  const rangeLengthDays =
    dateOf(currentValue[1]).diff(dateOf(currentValue[0]), "day") + 1;

  const handlePreset = (p: Preset) => {
    const [s, e] = getPresetRange(p, dayMode);
    onChange([s, e]);
  };

  // Периодът е от цели дни (краищата са точно границите на своите дни) →
  // стъпката смята границите на НОВИТЕ дни: работното време може да е
  // различно за всеки ден от седмицата. Ръчно въведен час (withTime) се
  // мести както е.
  const isWholeDays =
    currentValue[0].isSame(startOfDay(dateOf(currentValue[0]))) &&
    currentValue[1].isSame(endOfDay(dateOf(currentValue[1])));

  const shiftByDays = (days: number) => {
    if (isWholeDays) {
      onChange([
        startOfDay(dateOf(currentValue[0]).add(days, "day")),
        endOfDay(dateOf(currentValue[1]).add(days, "day")),
      ]);
      return;
    }
    onChange([currentValue[0].add(days, "day"), currentValue[1].add(days, "day")]);
  };

  const handleDecrement = () => {
    if (rangeIsFullMonth) {
      const newStart = dateOf(currentValue[0]).subtract(1, "month").startOf("month");
      onChange([startOfDay(newStart), endOfDay(newStart.endOf("month"))]);
      return;
    }
    shiftByDays(-rangeLengthDays);
  };

  const handleIncrement = () => {
    if (rangeIsFullMonth) {
      const newStart = dateOf(currentValue[0]).add(1, "month").startOf("month");
      onChange([startOfDay(newStart), endOfDay(newStart.endOf("month"))]);
      return;
    }
    shiftByDays(rangeLengthDays);
  };

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) return;
    let [start, end] = [dates[0], dates[1]];
    // Панелът е само с дати (изборът на ден от календар с час изисква
    // двойни кликове — rc-picker не прехвърля фокуса към крайното поле при
    // showTime). Клик върху ден носи час 00:00, тоест избраното е ДАТА, не
    // момент — разпъва се до целия работен ден на тази дата.
    //
    // При `withTime` изрично въведеният час (писане в полето) НЕ се пипа:
    // „18:00–23:00 за седмица" е валиден въпрос и BE-то го чете като час от
    // денонощието за всеки ден от периода.
    const startIsBareDate = start.hour() === 0 && start.minute() === 0;
    const endIsBareDate = end.hour() === 0 && end.minute() === 0;
    if (!withTime || startIsBareDate) start = startOfDay(start);
    if (!withTime || endIsBareDate) end = endOfDay(end);
    onChange([start, end]);
  };

  const isPlusDisabled = (() => {
    const today = dateOf(dayjs());
    const end = dateOf(currentValue[1]);
    return end.isSame(today, "day") || end.isAfter(today, "day");
  })();

  const presetDefs: { k: Preset; l: string }[] = [
    { k: "yesterday", l: t("reportsPage.periodYesterday") },
    { k: "today", l: t("reportsPage.periodToday") },
    { k: "week", l: t("reportsPage.period7d") },
    { k: "thisMonth", l: t("reportsPage.periodThisMonth") },
  ];

  return (
    <div
      className="rdp-root"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
      }}
    >
      <style>{`
        .rdp-seg {
          display: inline-flex;
          padding: 3px;
          background: ${R.surface};
          border: 1px solid ${R.borderMed};
          border-radius: 10px;
          height: 36px;
          gap: 2px;
          box-shadow: 0 1px 2px rgba(17,17,17,0.04);
        }
        .rdp-seg > button {
          border: none;
          background: transparent;
          color: ${R.textSoft};
          font-family: inherit;
          font-size: 11.5px;
          font-weight: 600;
          padding: 0 12px;
          border-radius: 7px;
          cursor: pointer;
          transition: background 120ms, color 120ms, box-shadow 120ms;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          line-height: 1;
        }
        .rdp-seg > button:hover:not(:disabled):not(.active) {
          color: ${R.ink};
          background: ${R.bg};
        }
        .rdp-seg > button.active {
          background: ${R.brandTint};
          color: ${R.brandDeep};
          box-shadow: inset 0 0 0 1px ${R.brandTint2};
        }

        .rdp-stepper {
          display: inline-flex;
          align-items: center;
          height: 36px;
          padding: 3px;
          background: ${R.surface};
          border: 1px solid ${R.borderMed};
          border-radius: 10px;
          gap: 2px;
          box-shadow: 0 1px 2px rgba(17,17,17,0.04);
        }
        .rdp-step-btn {
          width: 28px; height: 28px;
          border: none;
          background: transparent;
          color: ${R.textMuted};
          border-radius: 7px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background 120ms, color 120ms;
        }
        .rdp-step-btn:hover:not(:disabled) {
          background: ${R.bg};
          color: ${R.ink};
        }
        .rdp-step-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .rdp-picker .ant-picker {
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 8px !important;
          height: 28px !important;
        }
        .rdp-picker .ant-picker-input > input {
          font-size: 12.5px !important;
          color: ${R.ink} !important;
          font-weight: 600 !important;
          font-variant-numeric: tabular-nums;
        }
        .rdp-picker .ant-picker-input > input::placeholder { color: ${R.textMuted} !important; }
        .rdp-picker .ant-picker-range-separator { padding: 0 6px !important; color: ${R.textMuted} !important; }
        .rdp-picker .ant-picker-suffix { color: ${R.textMuted} !important; }
        .rdp-picker .ant-picker-active-bar { background: ${R.brand} !important; }
        .rdp-picker .ant-picker:hover { background: var(--color-shade-1) !important; border-radius: 7px !important; }
        .rdp-picker .ant-picker-focused { background: ${R.surface} !important; border-radius: 7px !important; box-shadow: 0 0 0 3px rgba(217,119,6,0.15) !important; }
      `}</style>

      {/* Presets segmented control */}
      <div className="rdp-seg" role="group">
        {presetDefs.map((p) => (
          <button
            key={p.k}
            type="button"
            className={activePreset === p.k ? "active" : ""}
            onClick={() => handlePreset(p.k)}
          >
            {p.l}
          </button>
        ))}
      </div>

      {/* Stepper + inline RangePicker */}
      <div className="rdp-stepper">
        <button
          type="button"
          className="rdp-step-btn"
          onClick={handleDecrement}
          title={t("reportsPage.periodPrev")}
          aria-label={t("reportsPage.periodPrev")}
        >
          <LeftOutlined style={{ fontSize: 11 }} />
        </button>
        <div
          className="rdp-picker"
          style={{
            minWidth: withTime
              ? isNarrow
                ? 280
                : 320
              : isNarrow
                ? 220
                : 240,
          }}
        >
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: R.brand,
                // Token математиката на antd не разбира var() → условна стойност.
                controlItemBgActive: isDark ? "#382a12" : "#FFF4E0",
              },
            }}
          >
            <RangePicker
              value={currentValue}
              onChange={handleRangeChange}
              format={withTime ? "DD.MM.YYYY HH:mm" : "DD.MM.YYYY"}
              allowClear={false}
              placeholder={[
                t("reportsPage.fromDate") || "От дата",
                t("reportsPage.toDate") || "До дата",
              ]}
              size="small"
            />
          </ConfigProvider>
        </div>
        <button
          type="button"
          className="rdp-step-btn"
          onClick={handleIncrement}
          title={t("reportsPage.periodNext")}
          aria-label={t("reportsPage.periodNext")}
          disabled={isPlusDisabled}
        >
          <RightOutlined style={{ fontSize: 11 }} />
        </button>
      </div>

    </div>
  );
};
